import type { Attachment, Entry, EntryKind, EntryList, EntryLocation, EntryState, Priority, Session, VoiceCapture } from "./types";

import { assertOfflineSession, captureOfflineSession, clearOfflineData, subscribeOfflineLogout } from "./offline";

let csrfToken: string | null = null;
const activeRequests = new Set<AbortController>();
subscribeOfflineLogout(() => {
  csrfToken = null;
  for (const controller of activeRequests) controller.abort();
});

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await requestWithResponse<T>(path, init)).data;
}

async function requestWithResponse<T>(path: string, init: RequestInit = {}): Promise<{ data: T; response: Response }> {
  const lease = path === "/api/v2/auth/session" || path === "/api/v2/auth/logout"
    ? null : captureOfflineSession();
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (lease) activeRequests.add(controller);
  init.signal?.addEventListener("abort", abort, { once: true });
  if (init.signal?.aborted) controller.abort();
  try {
    const headers = new Headers(init.headers);
    if (!(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
    if (csrfToken && init.method && init.method !== "GET") headers.set("X-CSRF-Token", csrfToken);
    const response = await fetch(path, { ...init, headers, signal: controller.signal, credentials: "same-origin" });
    if (lease) assertOfflineSession(lease);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      if (lease) assertOfflineSession(lease);
      if (lease && response.status === 401) await clearOfflineData().catch(() => {});
      const error = new Error(body?.error?.detail || body?.detail || "MemoHub konnte die Anfrage nicht ausführen.") as Error & { status: number; body: unknown };
      error.status = response.status; error.body = body; throw error;
    }
    const data = response.status === 204 ? undefined as T : await response.json() as T;
    if (lease) assertOfflineSession(lease);
    return { data, response };
  } catch (error) {
    if (lease) assertOfflineSession(lease);
    throw error;
  } finally {
    activeRequests.delete(controller);
    init.signal?.removeEventListener("abort", abort);
  }
}

export async function getSession(): Promise<Session> {
  const value = await request<Session>("/api/v2/auth/session"); csrfToken = value.csrf_token || null; return value;
}
export const listEntries = () => request<EntryList>("/api/v2/entries");
export interface EntryCreatePayload {
  kind?: EntryKind;
  title: string;
  markdown: string;
  tags: string[];
  favorite: boolean;
  icon_key?: string | null;
  state: EntryState;
  focus_date?: string | null;
  priority?: Priority;
  due_at?: string | null;
  recurrence?: Entry["recurrence"];
  location?: Pick<EntryLocation, "latitude" | "longitude" | "accuracy_meters" | "altitude_meters" | "altitude_accuracy_meters" | "heading_degrees" | "speed_meters_per_second" | "captured_at"> | null;
}
export async function createEntry(payload: EntryCreatePayload): Promise<{ entry: Entry; titleGenerationUnavailable: boolean }> {
  const result = await requestWithResponse<Entry>("/api/v2/entries", { method: "POST", body: JSON.stringify(payload) });
  return {
    entry: result.data,
    titleGenerationUnavailable: result.response.headers.get("X-MemoHub-Title-Generation") === "unavailable",
  };
}
export const patchEntry = (entry: Entry, changes: Partial<Entry>) => request<Entry>(`/api/v2/entries/${entry.uuid}`, {
  method: "PATCH", headers: { "If-Match": `"${entry.version}"` }, body: JSON.stringify({ expected_version: entry.version, ...changes }),
});
export const moveEntry = (entry: Entry, state: EntryState) => {
  const action = state === "today" ? "today" : state === "inbox" ? "inbox" : "complete";
  return request<Entry>(`/api/v2/entries/${entry.uuid}/${action}`, { method: "POST", body: JSON.stringify({ expected_version: entry.version }) });
};
export const completeEntry = (entry: Entry) => request<Entry>(`/api/v2/entries/${entry.uuid}/complete`, { method: "POST", body: JSON.stringify({ expected_version: entry.version }) });
export const reopenEntry = (entry: Entry) => request<Entry>(`/api/v2/entries/${entry.uuid}/reopen`, { method: "POST", body: JSON.stringify({ expected_version: entry.version }) });
export const archiveEntry = (entry: Entry) => request<Entry>(`/api/v2/entries/${entry.uuid}/archive`, { method: "POST", body: JSON.stringify({ expected_version: entry.version }) });
export const trashEntry = (entry: Entry) => request<Entry>(`/api/v2/entries/${entry.uuid}/trash`, { method: "POST", body: JSON.stringify({ expected_version: entry.version }) });
export const bulkEntryAction = (entries: Entry[], action: "archive" | "trash") =>
  request<{ items: Entry[] }>("/api/v2/entries/bulk-action", {
    method: "POST",
    body: JSON.stringify({
      action,
      items: entries.map((entry) => ({
        uuid: entry.uuid,
        expected_version: entry.version,
      })),
    }),
  });
export const searchEntries = (query: string) => request<Entry[]>(`/api/v2/search?q=${encodeURIComponent(query)}`);
export const logout = () => request<void>("/api/v2/auth/logout", { method: "POST" });
export const listAttachments = (entryUuid: string) => request<Attachment[]>(`/api/v2/entries/${entryUuid}/attachments`);
export const uploadAttachment = (entryUuid: string, file: File) => { const body = new FormData(); body.append("file", file); return request<Attachment>(`/api/v2/entries/${entryUuid}/attachments`, {method:"POST", body}); };
export const deleteAttachment = (entryUuid: string, attachmentUuid: string) => request<void>(`/api/v2/entries/${entryUuid}/attachments/${attachmentUuid}`, {method:"DELETE"});
export const attachmentUrl = (entryUuid: string, attachmentUuid: string) => `/api/v2/entries/${entryUuid}/attachments/${attachmentUuid}`;
export const webpushPublicKey = () => request<{public_key:string}>("/api/v2/push/public-key");
export const savePushSubscription = (subscription: PushSubscriptionJSON) => request<{uuid:string}>("/api/v2/push-subscriptions", {method:"POST", body:JSON.stringify({
  endpoint:subscription.endpoint, p256dh:subscription.keys?.p256dh, auth:subscription.keys?.auth, device_label:navigator.userAgent.includes("iPhone")?"iPhone":"Browser",
})});
export const removePushSubscription = (endpoint: string) => request<void>("/api/v2/push-subscriptions", {method:"DELETE", body:JSON.stringify({endpoint})});
export const sendTestPush = () => request<{subscriptions:number;badge_count:number}>("/api/v2/push/test", {method:"POST", body:"{}"});
export interface MemoSettings { theme: "system" | "light" | "dark"; timezone: string; push_enabled: boolean; conceal_push_titles: boolean }
export const getSettings = () => request<MemoSettings>("/api/v2/settings");
export const saveSettings = (settings: MemoSettings) => request<MemoSettings>("/api/v2/settings", {method:"PUT", body:JSON.stringify(settings)});
export const getReminder = (entryUuid: string) => request<{item: {remind_at:string;channel:string}|null}>(`/api/v2/entries/${entryUuid}/reminder`);
export const saveReminder = (entryUuid: string, remindAt: string | null) => request<{item: unknown}>(`/api/v2/entries/${entryUuid}/reminder`, {method:"PUT", body:JSON.stringify({remind_at:remindAt,channel:"webpush"})});
export const scheduleEntry = (entryUuid: string, durationMinutes: number) => request<Entry>(`/api/v2/entries/${entryUuid}/calendar/schedule`, {method:"POST", body:JSON.stringify({duration_minutes:durationMinutes})});
export const calendarWindow = (start: string, end: string) => request<{items?: CalendarItem[]}>(`/api/v2/calendar/window?start=${start}&end=${end}`);
export const createVoiceCapture = (audio: Blob) => {
  const body = new FormData();
  const extension = audio.type.includes("mp4") ? "m4a" : audio.type.includes("ogg") ? "ogg" : "webm";
  body.append("audio", audio, `memo-diktat.${extension}`);
  return request<VoiceCapture>("/api/v2/voice-captures", { method: "POST", body });
};
export const getVoiceCapture = (uuid: string) => request<VoiceCapture>(`/api/v2/voice-captures/${uuid}`);
export const discardVoiceCapture = (uuid: string) => request<void>(`/api/v2/voice-captures/${uuid}`, { method: "DELETE" });
export const applyVoiceCapture = (capture: VoiceCapture, proposal: NonNullable<VoiceCapture["proposal"]>, location?: EntryCreatePayload["location"]) =>
  request<Entry>(`/api/v2/voice-captures/${capture.uuid}/apply`, {
    method: "POST",
    body: JSON.stringify({
      intent: proposal.intent,
      title: proposal.title,
      markdown: proposal.markdown,
      due_at: proposal.due_at,
      priority: proposal.priority,
      icon_key: proposal.icon_key,
      tags: proposal.tags,
      location,
    }),
  });
export interface OfflineOperation { operation_id: string; kind: string; entry_uuid?: string; base_version?: number; payload: Record<string, unknown> }
export const syncOperations = (cursor: number, operations: OfflineOperation[]) => request<{cursor:number; operations:Array<{status:string;entry?:Entry;conflict_copy?:Entry}>;changes:Entry[]}>("/api/v2/sync", {
  method: "POST", body: JSON.stringify({ cursor, operations }),
});
export interface CalendarItem { uuid?: string; title: string; start_at: string; end_at?: string }
